import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, LineElementParams, PrimitiveElementDef } from '../../graphpad.model';
import type { CreatedElement, ElementContext, ElementCreator } from '../element.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../features/feature-style.model';
import { resolvePosition } from './position';

/**
 * Creates a line primitive between two positions.
 * Replaces the old ArrowCreator — arrows are just lines with `arrows: { last: true }` in display.
 *
 * Params define identity: from/to position references, optional name.
 * Display defines styling: color, strokeWidth, dash, arrows, visible.
 *
 * Exposes computed values: { fromX, fromY, toX, toY, length, midX, midY }
 */
export class LineCreator implements ElementCreator<PrimitiveElementDef> {
  readonly types = ['line'];

  create(
    id: string,
    def: PrimitiveElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { elementFactory, featureTheme } = context;
    const params = def.params as LineElementParams;
    const objects: GeometryElement[] = [];

    const [getFromX, getFromY] = resolvePosition(params.from, context);
    const [getToX, getToY] = resolvePosition(params.to, context);

    // Resolve line style from feature theme → display cascade
    const lineStyle = resolveFeatureStyle('line', 'line', context, display, []);
    const visible = resolveFeatureVisibility('line', featureTheme, display, []);

    // Create hidden anchor points
    const fromResult = elementFactory.point({
      coords: [getFromX, getFromY],
      style: { visible: false, size: 0 },
    });
    objects.push(fromResult.point);

    const toResult = elementFactory.point({
      coords: [getToX, getToY],
      style: { visible: false, size: 0 },
    });
    objects.push(toResult.point);

    // Create line with styling
    const lineResult = elementFactory.line({
      a: fromResult.point,
      b: toResult.point,
      name: params.name ?? id,
      style: {
        ...lineStyle,
        visible,
      },
    });
    objects.push(lineResult.line);
    if (lineResult.lineLabel) {
      objects.push(lineResult.lineLabel);
    }

    return {
      id,
      type: 'line',
      objects,
      getComputedValues: () => ({
        fromX: getFromX,
        fromY: getFromY,
        toX: getToX,
        toY: getToY,
        midX: () => (getFromX() + getToX()) / 2,
        midY: () => (getFromY() + getToY()) / 2,
        length: () => Math.sqrt((getToX() - getFromX()) ** 2 + (getToY() - getFromY()) ** 2),
        slope: () => {
          const dx = getToX() - getFromX();
          return dx === 0 ? NaN : (getToY() - getFromY()) / dx;
        },
      }),
      destroy: () => objects.forEach((obj) => context.board.removeObject(obj)),
    };
  }
}
